// src/pages/supplier/StorefrontPage.jsx
import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Store, Image, Globe, Instagram, MapPin, Phone, MessageCircle, Save, Eye, EyeOff, ExternalLink, Upload, X } from 'lucide-react'
import { supplierStorefrontApi } from '../../api/services'
import { Button, Card, CardBody, CardHeader, PageSpinner } from '../../components/common/ui'
import toast from 'react-hot-toast'

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa',
  'Gujarat','Haryana','Himachal Pradesh','Jammu & Kashmir','Jharkhand','Karnataka',
  'Kerala','Ladakh','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram',
  'Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
  'Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
]

export default function StorefrontPage() {
  const qc = useQueryClient()
  const bannerRef = useRef(null)
  const logoRef = useRef(null)

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-storefront'],
    queryFn: () => supplierStorefrontApi.show(),
  })

  const storefront = data?.data?.data || data?.data || null

  const [form, setForm] = useState(null)
  const [bannerPreview, setBannerPreview] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [bannerFile, setBannerFile] = useState(null)
  const [logoFile, setLogoFile] = useState(null)

  // Initialize form when data loads
  if (storefront && !form) {
    setForm({
      display_name: storefront.display_name || '',
      tagline: storefront.tagline || '',
      about: storefront.about || '',
      city: storefront.city || '',
      state: storefront.state || '',
      phone: storefront.phone || '',
      whatsapp: storefront.whatsapp || '',
      website: storefront.website || '',
      instagram_url: storefront.instagram_url || '',
      specialities: Array.isArray(storefront.specialities) ? storefront.specialities.join(', ') : (storefront.specialities || ''),
      gi_certified: storefront.gi_certified || false,
      verified_exporter: storefront.verified_exporter || false,
      years_in_business: storefront.years_in_business || '',
      meta_title: storefront.meta_title || '',
      meta_description: storefront.meta_description || '',
    })
  }

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }))

  const saveMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => {
        if (typeof v === 'boolean') fd.append(k, v ? '1' : '0')
        else fd.append(k, v ?? '')
      })
      if (bannerFile) fd.append('banner_image', bannerFile)
      if (logoFile) fd.append('logo_image', logoFile)
      fd.append('_method', 'PUT')
      return supplierStorefrontApi.update(fd)
    },
    onSuccess: () => {
      toast.success('Storefront saved!')
      qc.invalidateQueries(['supplier-storefront'])
      setBannerFile(null)
      setLogoFile(null)
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to save'),
  })

  const publishMutation = useMutation({
    mutationFn: () => supplierStorefrontApi.publish(),
    onSuccess: () => { toast.success('Storefront is now live!'); qc.invalidateQueries(['supplier-storefront']) },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to publish'),
  })

  const unpublishMutation = useMutation({
    mutationFn: () => supplierStorefrontApi.unpublish(),
    onSuccess: () => { toast.success('Storefront hidden from directory'); qc.invalidateQueries(['supplier-storefront']) },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to unpublish'),
  })

  const handleImageSelect = (e, type) => {
    const file = e.target.files?.[0]
    if (!file) return
    const url = URL.createObjectURL(file)
    if (type === 'banner') { setBannerFile(file); setBannerPreview(url) }
    else { setLogoFile(file); setLogoPreview(url) }
  }

  if (isLoading) return <PageSpinner />
  if (!form) return <PageSpinner />

  const isPublished = storefront?.is_published

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-display font-bold flex items-center gap-2">
            <Store size={20} className="text-pink" /> My Storefront
          </h1>
          <p className="text-sm text-muted mt-0.5">Customize how buyers see your shop</p>
        </div>
        <div className="flex items-center gap-3">
          {storefront?.slug && (
            <a href={`/suppliers/${storefront.slug}`} target="_blank" rel="noreferrer">
              <Button variant="secondary" size="sm"><ExternalLink size={14} /> Preview</Button>
            </a>
          )}
          {isPublished ? (
            <Button variant="secondary" size="sm" onClick={() => unpublishMutation.mutate()} loading={unpublishMutation.isPending}>
              <EyeOff size={14} /> Unpublish
            </Button>
          ) : (
            <Button size="sm" onClick={() => publishMutation.mutate()} loading={publishMutation.isPending}>
              <Eye size={14} /> Publish
            </Button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      <div className={`rounded-xl px-4 py-3 mb-6 text-sm font-medium flex items-center gap-2 ${isPublished ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
        <div className={`w-2 h-2 rounded-full ${isPublished ? 'bg-green-500' : 'bg-yellow-500'}`} />
        {isPublished ? 'Your storefront is live and visible to buyers' : 'Your storefront is not published yet — fill in the details below and publish when ready'}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate() }} className="space-y-6">
        {/* Banner & Logo */}
        <Card>
          <CardHeader>Images</CardHeader>
          <CardBody className="space-y-4">
            {/* Banner */}
            <div>
              <label className="text-sm font-bold text-gray-700 mb-2 block flex items-center gap-1.5">
                <Image size={14} /> Banner Image
                <span className="font-normal text-muted">(recommended 1200x400)</span>
              </label>
              <div
                className="relative h-40 rounded-xl border-2 border-dashed border-border bg-surface overflow-hidden cursor-pointer hover:border-pink transition-colors"
                onClick={() => bannerRef.current?.click()}
              >
                {(bannerPreview || storefront?.banner_image) ? (
                  <>
                    <img src={bannerPreview || storefront.banner_image} alt="Banner" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-sm font-bold">Change Banner</span>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-muted">
                    <Upload size={24} className="mb-1" />
                    <span className="text-xs">Click to upload banner</span>
                  </div>
                )}
              </div>
              <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={e => handleImageSelect(e, 'banner')} />
            </div>

            {/* Logo */}
            <div>
              <label className="text-sm font-bold text-gray-700 mb-2 block">Logo Image</label>
              <div className="flex items-center gap-4">
                <div
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-border bg-surface overflow-hidden cursor-pointer hover:border-pink transition-colors flex items-center justify-center"
                  onClick={() => logoRef.current?.click()}
                >
                  {(logoPreview || storefront?.logo_image) ? (
                    <img src={logoPreview || storefront.logo_image} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <Upload size={18} className="text-muted" />
                  )}
                </div>
                <div className="text-xs text-muted">
                  <p>Square image, max 2MB</p>
                  {(logoPreview || storefront?.logo_image) && (
                    <button type="button" className="text-pink hover:underline mt-1" onClick={() => logoRef.current?.click()}>Change</button>
                  )}
                </div>
              </div>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={e => handleImageSelect(e, 'logo')} />
            </div>
          </CardBody>
        </Card>

        {/* Basic Info */}
        <Card>
          <CardHeader>Basic Information</CardHeader>
          <CardBody className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-gray-700 mb-1 block">Shop Name *</label>
                <input className="input w-full" value={form.display_name} onChange={e => update('display_name', e.target.value)} placeholder="e.g. Sharma Silks" required />
              </div>
              <div>
                <label className="text-sm font-bold text-gray-700 mb-1 block">Tagline</label>
                <input className="input w-full" value={form.tagline} onChange={e => update('tagline', e.target.value)} placeholder="e.g. Premium silks since 1985" maxLength={200} />
              </div>
            </div>
            <div>
              <label className="text-sm font-bold text-gray-700 mb-1 block">About Your Business</label>
              <textarea className="input w-full" rows={4} value={form.about} onChange={e => update('about', e.target.value)} placeholder="Tell buyers about your business, history, specialties..." maxLength={3000} />
              <p className="text-xs text-muted mt-1 text-right">{form.about.length}/3000</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-gray-700 mb-1 block">Specialities</label>
                <input className="input w-full" value={form.specialities} onChange={e => update('specialities', e.target.value)} placeholder="e.g. Cotton, Silk, Handloom (comma-separated)" />
              </div>
              <div>
                <label className="text-sm font-bold text-gray-700 mb-1 block">Years in Business</label>
                <input type="number" className="input w-full" value={form.years_in_business} onChange={e => update('years_in_business', e.target.value)} min={0} placeholder="e.g. 15" />
              </div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="accent-pink w-4 h-4" checked={form.gi_certified} onChange={e => update('gi_certified', e.target.checked)} />
                <span className="text-sm font-medium">GI Certified</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="accent-pink w-4 h-4" checked={form.verified_exporter} onChange={e => update('verified_exporter', e.target.checked)} />
                <span className="text-sm font-medium">Verified Exporter</span>
              </label>
            </div>
          </CardBody>
        </Card>

        {/* Location & Contact */}
        <Card>
          <CardHeader>Location & Contact</CardHeader>
          <CardBody>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-gray-700 mb-1 block flex items-center gap-1"><MapPin size={13} /> City *</label>
                <input className="input w-full" value={form.city} onChange={e => update('city', e.target.value)} placeholder="e.g. Surat" />
              </div>
              <div>
                <label className="text-sm font-bold text-gray-700 mb-1 block">State</label>
                <select className="input w-full" value={form.state} onChange={e => update('state', e.target.value)}>
                  <option value="">Select state</option>
                  {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-700 mb-1 block flex items-center gap-1"><Phone size={13} /> Phone</label>
                <input className="input w-full" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+91 98765 43210" />
              </div>
              <div>
                <label className="text-sm font-bold text-gray-700 mb-1 block flex items-center gap-1"><MessageCircle size={13} /> WhatsApp</label>
                <input className="input w-full" value={form.whatsapp} onChange={e => update('whatsapp', e.target.value)} placeholder="+91 98765 43210" />
              </div>
              <div>
                <label className="text-sm font-bold text-gray-700 mb-1 block flex items-center gap-1"><Globe size={13} /> Website</label>
                <input type="url" className="input w-full" value={form.website} onChange={e => update('website', e.target.value)} placeholder="https://yoursite.com" />
              </div>
              <div>
                <label className="text-sm font-bold text-gray-700 mb-1 block flex items-center gap-1"><Instagram size={13} /> Instagram</label>
                <input type="url" className="input w-full" value={form.instagram_url} onChange={e => update('instagram_url', e.target.value)} placeholder="https://instagram.com/yourshop" />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* SEO */}
        <Card>
          <CardHeader>SEO Settings</CardHeader>
          <CardBody className="space-y-4">
            <div>
              <label className="text-sm font-bold text-gray-700 mb-1 block">Meta Title</label>
              <input className="input w-full" value={form.meta_title} onChange={e => update('meta_title', e.target.value)} placeholder="Custom page title for search engines" maxLength={120} />
              <p className="text-xs text-muted mt-1">{form.meta_title.length}/120 characters</p>
            </div>
            <div>
              <label className="text-sm font-bold text-gray-700 mb-1 block">Meta Description</label>
              <textarea className="input w-full" rows={2} value={form.meta_description} onChange={e => update('meta_description', e.target.value)} placeholder="Short description for search engine results" maxLength={300} />
              <p className="text-xs text-muted mt-1">{form.meta_description.length}/300 characters</p>
            </div>
          </CardBody>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <Button type="submit" loading={saveMutation.isPending}>
            <Save size={15} /> Save Changes
          </Button>
        </div>
      </form>
    </div>
  )
}
